"use client"

import { useMemo } from "react"
import { createPortal } from "react-dom"
import { Settings as SettingsIcon, UserPlus } from "lucide-react"
import { EntityTypeProvider } from "@/contexts/entity-type-context"
import { WorkflowProvider } from "@/context/workflow-context-simple"
import { WorkflowGrid } from "@/components/workflow-grid"
import { ViewSelector } from "@/components/view-selector"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useApi } from "@/lib/api"
import { useCodeValues } from "@/hooks/use-code-values"
import { useAtsConfig } from "@/hooks/ats/use-ats-config"
import { useAtsPlacementApi } from "@/hooks/ats/use-ats-placements-api"
import { useAtsCreateModal } from "@/hooks/ats/use-ats-create-modal"
import { useAtsSyncSettings } from "@/hooks/ats/use-ats-sync-settings"
import { AtsSyncSettingsModal } from "@/components/ats/sync-settings-modal"
import { AtsCreateModal } from "@/components/ats/create-modal"
import { useRequestsTabHeaderSlot, useRequestsTabOpenViewManager } from "@/contexts/requests-tab-header-slot-context"
import { useTranslation } from "@/hooks/use-translation"
import type { WorkflowStage } from "@/types/workflow"

/**
 * Renders the AtsPlacementRequest tab via the shared <WorkflowGrid> — same path
 * as {@link ClientRequestSubTabContent} / {@link VmsSubTabContent}. ATS follows the
 * same rules as the other Requests-group entities: field-config-driven columns,
 * shared search, global + per-tab filters, kebab actions from the WHC config.
 *
 * ATS-specific surfaces preserved from the bespoke tab, rendered into the shared
 * grid header via `headerExtraControls`:
 *   • "Create New ATS" — opens {@link AtsCreateModal} (uses the pure
 *     {@link useAtsPlacementApi} create call, so no bespoke data fetch).
 *   • Settings (gear) — opens {@link AtsSyncSettingsModal}.
 * "Show Completed" is rendered natively by the grid header when the ATS stage's
 * `showCompletedToggle` is true.
 */
export function AtsSubTabContent() {
  const { config, isLoading, error } = useAtsConfig()
  const { t } = useTranslation()
  const { atsServiceApi } = useApi()

  // Pure ATS API surface (useMemo, no data fetch) — gives us createAsync for the
  // Create New flow without pulling in the bespoke placement list.
  const atsApi = useAtsPlacementApi(atsServiceApi)
  const codeValues = useCodeValues()

  // The create flow needs the single ATS source code (e.g. "ProcomBullhorn").
  // Same derivation the bespoke tab used.
  const atsSource = useMemo<string | null>(() => {
    if (!codeValues.isReady) return null
    const sources = codeValues.getCodeValues("etl.CodeAtsSource", true)
    return sources.length === 1 ? sources[0].code : null
  }, [codeValues])

  const createModal = useAtsCreateModal(
    atsSource,
    async (source, atsPlacementId, force) => { await atsApi.createAsync(source, atsPlacementId, force) },
  )
  const syncSettings = useAtsSyncSettings(atsServiceApi)

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    )
  }

  const stage = config?.stage
  if (!config || !stage) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400">No stage configured for ATS Placements</p>
      </div>
    )
  }

  // Create New + Settings injected into the shared grid header via
  // `headerExtraControls` — rendered after Edit columns, before Filter. Order:
  // Show Completed · Edit columns · Create New ATS · Settings · Filter, matching
  // the VMS tab. The Settings gear reuses the VMS circular-icon-button styling
  // for a consistent look across tabs.
  const headerControls = (
    <>
      <Button variant="elliptical" onClick={createModal.onOpen} className="flex items-center gap-2">
        <UserPlus size={16} />
        {t("atsSyncCreateNewAts")}
      </Button>
      {syncSettings.canManage && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={syncSettings.onOpen}
                aria-label={t("atsSyncSettings") || "ATS Sync Settings"}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  border: "1px solid #BFC6D7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#fff",
                  cursor: "pointer",
                  color: "#0F214B",
                }}
              >
                <SettingsIcon size={20} strokeWidth={1.6} />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("atsSyncSettings") || "ATS Sync Settings"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  )

  return (
    <EntityTypeProvider value="AtsPlacementRequest">
      <WorkflowProvider initialConfiguration={config.configuration}>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            <WorkflowGrid
              stage={stage.stageCode as WorkflowStage}
              hidePinDock
              hideJumpToColumn
              headerExtraControls={headerControls}
            />
          </div>
        </div>
        <AtsViewSelectorPortal />
      </WorkflowProvider>

      {/* ATS-specific modals — preserved from the bespoke tab. */}
      <AtsSyncSettingsModal
        isOpen={syncSettings.isOpen}
        onClose={syncSettings.onClose}
        lobs={syncSettings.lobs}
        filteredClients={syncSettings.filteredClients}
        totalClientCount={syncSettings.totalClientCount}
        enabledCount={syncSettings.enabledCount}
        onSearchTextChange={syncSettings.setSearchText}
        filters={syncSettings.filters}
        duplicateAtsClientIds={syncSettings.duplicateAtsClientIds}
        draft={syncSettings.draft}
        defaultClient={syncSettings.defaultClient}
        getAllCheckboxState={syncSettings.getAllCheckboxState}
        onToggleLob={syncSettings.toggleLob}
        onToggleAll={syncSettings.toggleAll}
        filterChips={syncSettings.filterChips}
        applyToAll={syncSettings.applyToAll}
        onSave={syncSettings.save}
        hasChanges={syncSettings.hasChanges}
        changedClientIds={syncSettings.changedClientIds}
        isLoading={syncSettings.isLoading}
        isSaving={syncSettings.isSaving}
        error={syncSettings.error}
        orgDetailsLinkTemplate={
          config.configuration?.actionConfigurations?.find(
            (a: any) => a.actionCode === "ClientLink"
          )?.link
        }
      />
      <AtsCreateModal
        isOpen={createModal.isOpen}
        atsPlacementId={createModal.atsPlacementId}
        onAtsPlacementIdChange={createModal.onAtsPlacementIdChange}
        error={createModal.error}
        duplicate={createModal.duplicate}
        onClose={createModal.onClose}
        onConfirm={createModal.onConfirm}
        onForce={createModal.onForce}
      />
    </EntityTypeProvider>
  )
}

/**
 * Portals the ViewSelector into the workflow-hub top-right slot. Lives inside the
 * WorkflowProvider so ViewSelector can read useWorkflow(); createPortal decouples
 * tree position from render location. (Settings + Create New live in the grid
 * header via headerExtraControls, matching the bespoke tab's toolbar placement.)
 */
function AtsViewSelectorPortal() {
  const slot = useRequestsTabHeaderSlot()
  // Enables the "View Designer" entry on this tab — see ClientRequestSubTabContent's portal.
  const onOpenViewManager = useRequestsTabOpenViewManager()
  if (!slot) return null
  return createPortal(<ViewSelector onOpenViewManager={onOpenViewManager ?? undefined} />, slot)
}
